import axios from "axios";
import { privateFetch } from "../../utils/CustomFetch";
import { CONSTANT } from "../../utils/Constant";

const BASE_URL = "genia/api/v1";

// Si hay GENIA local configurada, usar axios directo (GENIA no requiere auth)
const geniaFetch = window.REACT_APP_GENIA_URL
    ? axios.create({ baseURL: window.REACT_APP_GENIA_URL })
    : privateFetch;
const GENIA_BASE = window.REACT_APP_GENIA_URL ? "" : BASE_URL;

/**
 * Convierte mensajes de error técnicos del backend en mensajes amigables
 */
const friendlyErrorMessage = (message) => {
    if (!message) return "Error al procesar la solicitud. Intente nuevamente.";
    const lower = message.toLowerCase();
    if (lower.includes("token oauth expirado") || lower.includes("invalid_grant") || lower.includes("claude login")) {
        return "El servicio de IA no está disponible temporalmente. Por favor contacte al administrador.";
    }
    if (lower.includes("timeout") || lower.includes("timed out")) {
        return "La generación tardó demasiado. Intente con una transcripción más corta o inténtelo de nuevo.";
    }
    if (lower.includes("rate limit") || lower.includes("too many requests")) {
        return "Demasiadas solicitudes. Espere un momento antes de intentar nuevamente.";
    }
    if (lower.includes("token") || lower.includes("auth") || lower.includes("credential")) {
        return "Error de autenticación con el servicio de IA. Por favor contacte al administrador.";
    }
    return message;
};

/**
 * Servicio para el generador de propuestas
 * Todas las llamadas pasan por Gateway → GENIA (Python/FastAPI)
 *
 * Flujo de generación:
 * 1. generateRawProposal() - Genera propuesta con IA desde transcripción (3-5 min)
 * 2. renderHtml() - Convierte propuesta a HTML formateado
 * 3. renderPdf() - Genera PDF desde la propuesta
 */

/**
 * Generar propuesta completa con IA desde transcripción
 * Envía la transcripción a Claude (Plan Pro Max) vía GENIA
 * y recibe la propuesta en formato markdown
 *
 * @param {Object} data - { text: transcripción, company_name: nombre, country: país }
 * @returns {Promise<Object>} - { proposal_text, sections, success, message }
 */
export const generateRawProposal = async (data) => {
    const requestBody = {
        clientName: data.client_name || data.clientName || "",
        clientCompany: data.company_name || data.clientCompany || "",
        projectName: data.project_name || data.projectName || "Propuesta Comercial",
        country: data.country || data.country_name || "",
        projectType: data.projectType || data.project_type || "",
        transcription: data.text || data.transcription || "",
        clientEmail: data.client_email || data.clientEmail || "",
        clientPhone: data.client_phone || data.clientPhone || "",
        customPrompt: data.customPrompt || data.custom_prompt || null,
    };

    const response = await geniaFetch.post(`${GENIA_BASE}/generate/raw`, requestBody, {
        headers: { ...CONSTANT.HEADER_TYPE },
        timeout: 300000,
    });

    const proposalText = response.data.rawProposalText || response.data.raw_proposal_text || response.data.markdown || response.data.proposal_text || "";
    const isSuccess = response.data.status === "SUCCESS" || response.data.success === true || !!proposalText;

    const rawMessage = response.data.message || (isSuccess ? "Propuesta generada exitosamente" : "Error al generar propuesta");

    return {
        proposal_text: proposalText,
        proposal_name: response.data.sections?.[0]?.title || response.data.proposalName || "Propuesta Generada",
        sections: response.data.sections || [],
        success: isSuccess,
        message: isSuccess ? rawMessage : friendlyErrorMessage(rawMessage),
    };
};

/**
 * Generar propuesta completa con IA (alias de generateRawProposal)
 */
export const generateProposal = async (data) => {
    return generateRawProposal(data);
};

/**
 * Generar contenido para un paso específico del multistep
 *
 * @param {string} step - ID del paso (cover, context, architecture, etc.)
 * @param {Object} data - Datos del paso actual y anteriores
 * @returns {Promise<Object>} - Contenido generado para el paso
 */
export const generateStep = async (step, data) => {
    const requestBody = {
        stepId: step,
        stepData: data.stepData || {},
        step1: data.step1 || {},
        rawProposalText: data.rawProposalText || "",
        previousSteps: data.previousSteps || {},
    };

    const response = await geniaFetch.post(`${GENIA_BASE}/generate/step/${step}`, requestBody, {
        headers: { ...CONSTANT.HEADER_TYPE },
        timeout: 180000,
    });

    const generatedContent = response.data.generatedContent || response.data.generated_content || response.data.content || {};
    const isSuccess = response.data.status === "SUCCESS" || response.data.success === true || !!Object.keys(generatedContent).length;

    const rawStepMessage = response.data.message || (isSuccess ? `Step ${step} generado` : `Error en step ${step}`);

    return {
        step: response.data.step || step,
        generatedContent: generatedContent,
        success: isSuccess,
        message: isSuccess ? rawStepMessage : friendlyErrorMessage(rawStepMessage),
    };
};

/**
 * Iniciar generacion completa de propuesta en background (asincronica)
 * Retorna job_id inmediatamente; usar getProposalStatus para hacer polling.
 */
export const startProposalGeneration = async (data) => {
    const requestBody = {
        clientName: data.clientName || "",
        clientCompany: data.clientCompany || "",
        projectName: data.clientCompany || data.clientName || "Propuesta Comercial",
        country: data.country || "",
        projectType: data.projectType || data.project_type || "",
        transcription: data.transcription || "",
        customPrompt: data.customPrompt || null,
    };

    const response = await geniaFetch.post(`${GENIA_BASE}/generate/proposal/start`, requestBody, {
        headers: { ...CONSTANT.HEADER_TYPE },
        timeout: 15000,
    });

    return { jobId: response.data.jobId || response.data.job_id };
};

/**
 * Consultar el estado de un job de generacion asincronica
 */
export const getProposalStatus = async (jobId) => {
    const response = await geniaFetch.get(`${GENIA_BASE}/generate/proposal/status/${jobId}`, {
        headers: { ...CONSTANT.HEADER_TYPE },
        timeout: 10000,
    });

    return {
        status: response.data.status,
        progress: response.data.progress || 0,
        rawText: response.data.rawText || response.data.raw_text || null,
        steps: response.data.steps || {},
        error: response.data.error || null,
    };
};

/**
 * Regenerar sección específica con IA
 */
export const regenerateSection = async (sectionType, data) => {
    const requestBody = {
        sectionType: sectionType,
        stepData: data.stepData || data,
        customPrompt: data.customPrompt || data.custom_prompt || null,
    };

    const response = await geniaFetch.post(`${GENIA_BASE}/sections/regenerate`, requestBody, {
        headers: { ...CONSTANT.HEADER_TYPE },
        timeout: 180000,
    });

    return {
        type: response.data.type,
        title: response.data.title,
        content: response.data.contentMarkdown || response.data.content_markdown,
        html: response.data.contentHtml || response.data.content_html,
        order: response.data.order,
    };
};

/**
 * Mejorar sección existente con IA
 */
export const improveSection = async (sectionType, data) => {
    const requestBody = {
        sectionType: sectionType,
        stepData: data.stepData || data,
        customPrompt: data.customPrompt || data.custom_prompt || null,
    };

    const response = await geniaFetch.post(`${GENIA_BASE}/sections/improve`, requestBody, {
        headers: { ...CONSTANT.HEADER_TYPE },
        timeout: 180000,
    });

    return {
        type: response.data.type,
        title: response.data.title,
        content: response.data.contentMarkdown || response.data.content_markdown,
        html: response.data.contentHtml || response.data.content_html,
        order: response.data.order,
    };
};

/**
 * Renderizar propuesta a HTML
 */
export const renderHtml = async (proposalData) => {
    const requestBody = {
        stepData: proposalData.stepData || proposalData,
        generatedContent: proposalData.generatedContent || proposalData.generated_content || null,
        stepsToRender: proposalData.stepsToRender || null,
    };

    const response = await geniaFetch.post(`${GENIA_BASE}/render/html`, requestBody, {
        headers: { ...CONSTANT.HEADER_TYPE },
        timeout: 60000,
    });

    return typeof response.data === 'string' ? response.data : response.data.html;
};

/**
 * Generar PDF desde propuesta
 */
export const renderPdf = async (proposalData) => {
    const requestBody = {
        stepData: proposalData.stepData || proposalData,
        generatedContent: proposalData.generatedContent || proposalData.generated_content || null,
        stepsToRender: proposalData.stepsToRender || null,
    };

    const response = await geniaFetch.post(`${GENIA_BASE}/render/pdf`, requestBody, {
        headers: { ...CONSTANT.HEADER_TYPE },
        responseType: 'blob',
        timeout: 120000,
    });

    return response.data;
};

/**
 * Generar versión ejecutiva del PDF (2 páginas: portada + resumen ejecutivo)
 */
export const renderPdfExecutive = async (proposalData) => {
    const requestBody = {
        stepData: proposalData.stepData || proposalData,
        executive: true,
    };

    const response = await geniaFetch.post(`${GENIA_BASE}/render/pdf`, requestBody, {
        headers: { ...CONSTANT.HEADER_TYPE },
        responseType: 'blob',
        timeout: 120000,
    });

    return response.data;
};

/**
 * Calcular precios (local, sin API)
 */
export const calculatePricing = async (pricingData) => {
    const hours = pricingData.hours || pricingData.estimatedHours || 340;
    const hourlyRate = pricingData.hourlyRate || 31;
    const taxRate = pricingData.taxPercentage || pricingData.tax_percentage || 0.13;

    const subtotal = hours * hourlyRate;
    const tax = subtotal * taxRate;

    return {
        estimatedHours: hours,
        hourlyRate: hourlyRate,
        subtotal: subtotal,
        tax: tax,
        total: subtotal + tax,
        currency: pricingData.currency || "USD",
    };
};

// ==========================================
// CRUD de propuestas (Maintenance API / BD)
// ==========================================

const PROPOSAL_BASE = "maintenance/proposal";

export const saveProposal = async (proposalData) => {
    const response = await privateFetch.post(PROPOSAL_BASE, proposalData, {
        headers: { ...CONSTANT.HEADER_TYPE },
    });
    return response.data;
};

export const updateProposal = async (id, proposalData) => {
    const response = await privateFetch.put(`${PROPOSAL_BASE}/${id}`, proposalData, {
        headers: { ...CONSTANT.HEADER_TYPE },
    });
    return response.data;
};

export const getProposal = async (id) => {
    const response = await privateFetch.get(`${PROPOSAL_BASE}/${id}`, {
        headers: { ...CONSTANT.HEADER_TYPE },
    });
    return response.data;
};

export const listProposals = async (userId, role, page = 0, size = 25) => {
    const response = await privateFetch.get(
        `${PROPOSAL_BASE}?userId=${userId}&role=${role}&page=${page}&size=${size}&sort=createdAt,desc`,
        { headers: { ...CONSTANT.HEADER_TYPE } }
    );
    return response.data;
};

export const deleteProposal = async (id) => {
    await privateFetch.delete(`${PROPOSAL_BASE}/${id}`, {
        headers: { ...CONSTANT.HEADER_TYPE },
    });
};

/**
 * Validar datos del paso
 */
export const validateStep = async (step, data) => {
    const requiredFields = {
        1: ['clientName', 'transcription'],
        2: ['coverTitle'],
        3: ['projectContext'],
        4: ['scope'],
        5: ['proposedSolution'],
        6: ['pricing'],
    };

    const missing = (requiredFields[step] || []).filter(field => !data[field]);

    return {
        valid: missing.length === 0,
        missing,
    };
};
